import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { DailyLog } from '../services/logs';
import { LearningStream } from '../services/streams';

interface ProgressChartProps {
  logs: DailyLog[];
  streams: LearningStream[];
}

const ProgressChart: React.FC<ProgressChartProps> = ({ logs, streams }) => {
  // Group logs by date and stream
  const chartData = React.useMemo(() => {
    const dataMap: { [key: string]: { [key: string]: number | string; date: string } } = {};
    
    logs.forEach(log => {
      const date = new Date(log.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      if (!dataMap[date]) {
        dataMap[date] = { date };
      }
      const streamName = streams.find(s => s.id === log.stream_id)?.name || 'Unknown';
      const currentValue = typeof dataMap[date][streamName] === 'number' 
        ? (dataMap[date][streamName] as number) 
        : 0;
      dataMap[date][streamName] = currentValue + log.hours_studied;
    });

    return Object.values(dataMap);
  }, [logs, streams]);

  const streamNames = streams.map(s => s.name);

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-xl font-semibold mb-4">Weekly Study Hours</h3>
      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            {streamNames.map((name, index) => (
              <Bar
                key={name}
                dataKey={name}
                fill={index === 0 ? '#3b82f6' : index === 1 ? '#22c55e' : index === 2 ? '#a855f7' : '#f59e0b'}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="flex items-center justify-center h-64 text-gray-500">
          No data available yet. Start logging your study hours!
        </div>
      )}
    </div>
  );
};

export default ProgressChart;

